import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import 'react-loading-skeleton/dist/skeleton.css';
import {User} from '../../../entity/model/user';
import {useService} from '../../../infrastructure/service/service';
import {UserPermission} from '../../../entity/enums/user-permission';
import {Organization} from '../../../entity/model/organization';
import {ManageMode} from '../../../entity/enums/manage-mode';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import {
  Button, Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table, TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import TableSortLabel from '@mui/material/TableSortLabel';
import LoadingComponent from '../components/widget/LoadingComponent';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment/moment';
import Skeleton from 'react-loading-skeleton';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ConfirmDialog from '../components/widget/ConfirmDialog';
import UserDialog from '../components/users/UserDialog';
import TablePagination from '../components/widget/TablePagination';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Form, Formik, FormikProps} from 'formik';
import {SearchInputDto} from '../../../entity/dto/search-input-dto';

import '../../../assets/css/style.css';

const Users = (): JSX.Element => {
  const {userService, organizationService} = useService();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [createUserPermission, setCreateUserPermission] = useState<boolean>(false);
  const [updateUserPermission, setUpdateUserPermission] = useState<boolean>(false);
  const [deleteUserPermission, setDeleteUserPermission] = useState<boolean>(false);
  const [organization, setOrganization] = useState<Organization>();
  const [mode, setMode] = useState<ManageMode>(ManageMode.VIEW);
  const [openUserDialog, setOpenUserDialog] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [users, setUsers] = useState<User[]>([]);
  const [searchUsers, setSearchUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSearchInput, setCurrentSearchInput] = useState<string>('');

  const pageSize = 100;
  const tableHeaders = ['First Name', 'Last Name', 'Email', 'Organization', 'Role(s)', 'Created', 'User ID'];

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      setCreateUserPermission(userService.isPermitted(UserPermission.CREATE_ORG_USER));
      setUpdateUserPermission(userService.isPermitted(UserPermission.UPDATE_ORG_USER));
      setDeleteUserPermission(userService.isPermitted(UserPermission.DELETE_ORG_USER));

      setOrganization(await organizationService.getOrganizationById(user?.organizationID ?? ''));

      const users = await userService.getAllUsers(user?.organizationID ?? '', false);
      setUsers(users);
      setSearchUsers(users.slice(0, pageSize));
      setTotalPages(Math.ceil(users.length / pageSize));
    }

    init().then(() => setLoading(false));
  }, [userService.getCache()])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User): void => {
    setMenuElement(event.currentTarget);
    setSelectedUser(user);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handleSearchChange = (handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    handleChange(event);

    const input = event.target.value.trim();
    setCurrentSearchInput(input);

    const inputLower = input.toLowerCase();
    if (!inputLower) {
      setCurrentPage(1);
      setTotalPages(Math.ceil(users.length / pageSize));
      setSearchUsers(users.slice(0, pageSize));
      return;
    }

    const filteredUsers = userService.filterUsers(inputLower);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredUsers.length / pageSize));
    setSearchUsers(filteredUsers.slice(0, pageSize));
  };

  const handlePaginationChange = (event: React.ChangeEvent<unknown>, activePage: number): void => {
    setCurrentPage(activePage);

    const startIndex = (activePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setSearchUsers(users.slice(startIndex, endIndex));
  }

  const handleOpenUserDialog = (mode: ManageMode): void => {
    if (mode !== ManageMode.CREATE)
      handleMenuClose();
    else
      setSelectedUser(currentUser);

    setMode(mode);
    setOpenUserDialog(true);
  }

  const handleDeleteUser = (): void => {
    handleMenuClose();
    setOpenConfirmDialog(true);
  }

  const deleteUser = async (): Promise<void> => {
    setSubmitLoading(true);

    await userService.deleteUser(selectedUser?.organizationID ?? '', selectedUser?.id ?? '');

    await refreshPage();
    setSubmitLoading(false);
    setOpenConfirmDialog(false);
  }

  const handleCopyUser = async (): Promise<void> => {
    handleMenuClose();

    await navigator.clipboard.writeText(selectedUser?.id ?? '');
    toast.success('User ID copied to clipboard !!');
  }

  const handleDetachUser = async (): Promise<void> => {
    handleMenuClose();
    
    await userService.detachUser(selectedUser?.organizationID ?? '', selectedUser?.id ?? '');
    await refreshPage();
  }

  const refreshPage = async (): Promise<void> => {
    setRefresh(true);

    const users = await userService.getAllUsers(currentUser?.organizationID ?? '', true);
    setUsers(users);

    if (currentSearchInput && currentSearchInput.trim()) {
      const filteredUsers = userService.filterUsers(currentSearchInput.toLowerCase());
      setSearchUsers(filteredUsers.slice(0, pageSize));
      setTotalPages(Math.ceil(filteredUsers.length / pageSize));
    } else {
      setSearchUsers(users.slice(0, pageSize));
      setTotalPages(Math.ceil(users.length / pageSize));
    }

    setCurrentPage(1);
    setRefresh(false);
  }

  const renderTableRow = (user: User, index: number): JSX.Element => {
    return (
      <TableRow
        hover
        tabIndex={-1}
        key={user.id}
        className={'table-cell'}>
        <TableCell className={'no-wrap'} padding='checkbox'>
          <IconButton
            id={`btn-user-menu-${index}`}
            className={'zitles-menu-trigger'}
            color= "inherit"
            disableRipple
            disableFocusRipple
            onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, user)}>
            <MoreHorizIcon />
          </IconButton>
          <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
            <MenuItem
              id={`menu-user-view-${index}`}
              disableRipple
              className={'zitles-menu-item'}
              onClick={(): void => handleOpenUserDialog(ManageMode.VIEW)}>
              <VisibilityIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>View</Typography>
            </MenuItem>
            {
              updateUserPermission &&
                <MenuItem
                  id={`menu-user-edit-${index}`}
                  disableRipple
                  className={'zitles-menu-item'}
                  onClick={(): void => handleOpenUserDialog(ManageMode.UPDATE)}>
                  <EditIcon className={'zitles-icon'} fontSize={'small'} sx={{marginLeft: '5px'}}/>
                  <Typography className={'zitles-menu-item button'}>Edit</Typography>
                </MenuItem>
            }
            <MenuItem
              id={`menu-user-copy-${index}`}
              disableRipple
              className={'zitles-menu-item'}
              onClick={handleCopyUser}>
              <FileCopyIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>Copy</Typography>
            </MenuItem>
            {
              currentUser && selectedUser && selectedUser.id !== currentUser.id &&
                <MenuItem
                  id={`menu-user-detach-${index}`}
                  disableRipple
                  className={'zitles-menu-item'}
                  onClick={handleDetachUser}>
                  <LinkOffIcon className={'zitles-icon'}/>
                  <Typography className={'zitles-menu-item button'}>Detach</Typography>
                </MenuItem>
            }
            {
              deleteUserPermission &&
                <Box>
                  <Divider/>
                  <MenuItem
                    id={`menu-user-delete-${index}`}
                    disableRipple
                    className={'zitles-menu-item'}
                    onClick={handleDeleteUser}>
                    <DeleteIcon className={'zitles-icon'}/>
                    <Typography className={'zitles-menu-item button'}>Delete</Typography>
                  </MenuItem>
                </Box>
            }
          </Menu>
        </TableCell>
        <TableCell id={`text-user-first-name-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{user.firstName}</TableCell>
        <TableCell id={`text-user-last-name-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{user.lastName}</TableCell>
        <TableCell id={`text-user-email-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{user.email}</TableCell>
        <TableCell id={`text-user-organization-name-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{organization?.name ?? <Skeleton/>}</TableCell>
        <TableCell id={`text-user-roles-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{user.roles.join(', ')}</TableCell>
        <TableCell id={`text-user-created-date-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{moment.utc(user.createdAt).format('YYYY-MM-DD')}</TableCell>
        <TableCell id={`text-user-id-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{user.id}</TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {
        loading
          ? <LoadingComponent/>
          : <Box className={'body-box'}>
            {
              refresh &&
                <LoadingComponent/>
            }
            <Paper className={'table-container'} elevation={0}>
              <Box className={'toolbar-container'}>
                {
                  createUserPermission &&
                    <Button
                      id={'btn-user-create'}
                      className={'zitles-button'}
                      variant="contained"
                      startIcon={<AddIcon/>}
                      disabled={!createUserPermission}
                      onClick={(): void => handleOpenUserDialog(ManageMode.CREATE)}>
                      New User
                    </Button>
                }
                {
                  createUserPermission && organization?.isPublic === true &&
                    <Button
                      id={'btn-user-invitations'}
                      className={'zitles-button user-invitations-button'}
                      variant="outlined"
                      onClick={(): void => navigate('/user-invitations')}>
                      User Invitations
                    </Button>
                }
                <Formik initialValues={userService.getSearchDto()} onSubmit={(): void => {}}>
                  {({values, handleChange}: FormikProps<SearchInputDto>): JSX.Element => (
                    <Form>
                      <TextField
                        id={'search-user'}
                        name='searchInput'
                        className={createUserPermission ? 'zitles-searchbar' : 'zitles-searchbar no-padding'}
                        variant={'outlined'}
                        placeholder="Search"
                        value={values.searchInput}
                        onChange={handleSearchChange(handleChange)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon className={'zitles-searchbar icon'}/>
                            </InputAdornment>)
                        }}/>
                    </Form>
                  )}
                </Formik>
                <Button
                  id={'btn-user-refresh'}
                  className={'zitles-button zitles-table refresh'}
                  variant='contained'
                  startIcon={<RefreshIcon />}
                  disabled={refresh}
                  onClick={refreshPage}>
                  Refresh
                </Button>
              </Box>
              {
                searchUsers.length > 0
                  ? <TableContainer className={'zitles-table'} component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow className={'table-header'}>
                            <TableCell className={'no-wrap'} padding="checkbox"/>
                            {
                              tableHeaders.map((header: string): JSX.Element => (
                                <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                                  <TableSortLabel
                                    id={`user-column-${header}`}
                                    className={'table-header-text'}
                                    active={true}
                                    direction='desc'>
                                    {header}
                                  </TableSortLabel>
                                </TableCell>
                              ))
                            }
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            searchUsers.map(renderTableRow)
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                  : <Typography id={'text-user-empty'} className='empty-message'>No users found !!</Typography>
              }
            </Paper>
            {
              searchUsers.length > 0 &&
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={handlePaginationChange}/>
            }
          </Box>
      }
      {
        openUserDialog &&
          <UserDialog
            open={openUserDialog}
            setOpen={setOpenUserDialog}
            mode={mode}
            user={selectedUser}/>
      }
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={'Delete User'}
            message={'Are you sure you want to delete this user?'}
            handleSubmit={deleteUser}
            loading={submitLoading}/>
      }
    </>
  )
}

export default observer(Users);
